import React, { lazy, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
// Кит — первым из стилей: стили шелла перебивают его на равной специфичности
// (окно звонка: .call-modal против .modal), а порядок в index.css — порядок
// импорта. Раньше кит первой подтягивала страница вакансий через nav.tsx; с
// разбиением страницы ушли в свои куски, кит съехал в конец index.css, и окно
// звонка стало шириной с модалку кита (520px вместо 420).
import "@/styles/kit.css";
import { router } from "@/router";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { LoginPage } from "@/auth/LoginPage";
import { UploadProvider } from "@/uploads/UploadManager";
import { UploadTray } from "@/uploads/UploadTray";
import { isMobilePath, isPhone, redirectPhoneToMobile } from "@/mobile/device";
import { Unreachable } from "@/mobile/Unreachable";
import { inTrayHost, isAppPath, isInterviewsPath, isToolWindowPath, isTrayPath, postToHost } from "@/tray/bridge";
import { TrayOffline } from "@/tray/TrayOffline";
import { useRecheckAuth } from "@/tray/useRecheckAuth";
import { UpdateBanner } from "@/components/UpdateBanner";
import { ChunkBoundary } from "@/components/ChunkFailed";
import { onChunkError, startUpdateWatch } from "@/lib/appUpdate";
// Слушатели боковой панели расширения HRDeva — с загрузки приложения, а не с
// захода в чаты. Content script шлёт hello и «панель открыта» один раз, на
// старте вкладки, а открывается она обычно на /vacancies. Модуль нужен только
// чатам и сам уехал бы в их кусок — тогда оба события терялись: чаты рисовали
// свою панель рядом с панелью расширения, пока ту не переключат.
import "@/lib/extensionPanel";
import "@/styles/theme.css";

// Экраны не для рекрутёра за работой — публичная ссылка для заказчика и
// заглушка «откройте на телефоне» (с QR-кодом: библиотека qrcode) — отдельными
// кусками: остальным их код не нужен.
const PublicCandidatePage = lazy(() =>
  import("@/pages/PublicCandidatePage").then((m) => ({ default: m.PublicCandidatePage }), onChunkError),
);
const VacancyClientPage = lazy(() =>
  import("@/pages/ClientView/VacancyClientView").then(
    (m) => ({ default: m.VacancyClientView }),
    onChunkError,
  ),
);
const PublicCheckPage = lazy(() =>
  import("@/pages/PublicCheckPage").then((m) => ({ default: m.PublicCheckPage }), onChunkError),
);
const PhoneOnly = lazy(() => import("@/mobile/PhoneOnly").then((m) => ({ default: m.PhoneOnly }), onChunkError));

const loading = <div className="app-loading">Загрузка…</div>;

function Gate() {
  const { state, retry } = useAuth();
  // Окно программы «HRDeva» (tray/): ей нужно знать, вошёл ли
  // пользователь, — показать форму входа или спрятать окно до звонка.
  const tray = isTrayPath(window.location.pathname) && inTrayHost();
  const authedName = state.status === "authed" ? state.user.full_name || state.user.email : undefined;
  useEffect(() => {
    if (!tray || state.status === "loading") return;
    postToHost({ type: "auth", status: state.status, user: authedName });
  }, [tray, state.status, authedName]);
  useRecheckAuth(tray, state.status === "authed", retry);
  if (state.status === "loading") {
    return loading;
  }
  if (state.status === "unreachable" && tray) {
    return <TrayOffline onRetry={retry} note={isInterviewsPath(window.location.pathname) || isAppPath(window.location.pathname)} />;
  }
  // Телефон без связи (или сервер на выкатке) — не форма входа, а «нет
  // связи» с повтором: установленное приложение открывают где угодно.
  if (state.status === "unreachable" && isMobilePath(window.location.pathname)) {
    return <Unreachable onRetry={retry} />;
  }
  if (state.status === "anon" || state.status === "unreachable") {
    return <LoginPage />;
  }
  // Upload state lives ABOVE the router so uploads survive navigation
  // between pages (kandidat → vacancy → back stays live).
  return (
    <UploadProvider>
      {/* Первый экран ждёт куска своей страницы (router.tsx) — под той же заглушкой. */}
      <RouterProvider router={router} fallbackElement={loading} />
      {/* В окнах программы «Транскрибировать файл…» и «Инструменты» лотка нет:
          он висит в углу поверх всего, а окно размером с него самого — лоток
          закрыл бы список заданий вместе с кнопкой «Скачать». Ход загрузки там
          видно в самой странице (tray/TranscribePage). В едином окне
          (/tray/app) лоток есть — вложения в карточке кандидата грузятся
          через него, — но пока открыт раздел «Инструменты», его прячет CSS
          (tray.css). */}
      {!isToolWindowPath(window.location.pathname) && <UploadTray />}
    </UploadProvider>
  );
}

// Public client-facing share link — mounted before AuthProvider so the
// unauthenticated reader never gets bounced to the login screen.
function Root() {
  return (
    <>
      <Screen />
      {/* Окну программы плашка не нужна: оно перезагружается само, пока
          скрыто, а видно его только во время звонка — плашка легла бы на окно
          звонка. */}
      {!(typeof window !== "undefined" && isTrayPath(window.location.pathname)) && <UpdateBanner />}
    </>
  );
}

function Screen() {
  // Страница заказчика по общей ссылке вакансии: список добавленных кандидатов.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/v/")) {
    const token = window.location.pathname.slice(3).replace(/\/+$/, "");
    return (
      <ChunkBoundary className="app-loading">
        <Suspense fallback={loading}>
          <VacancyClientPage token={token} />
        </Suspense>
      </ChunkBoundary>
    );
  }
  // Тест или тестовое задание кандидата по личной ссылке (PRI-171).
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/t/")) {
    const token = window.location.pathname.slice(3).replace(/\/+$/, "");
    return (
      <ChunkBoundary className="app-loading">
        <Suspense fallback={loading}>
          <PublicCheckPage token={token} />
        </Suspense>
      </ChunkBoundary>
    );
  }
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/s/")) {
    const token = window.location.pathname.slice(3);
    return (
      <ChunkBoundary className="app-loading">
        <Suspense fallback={loading}>
          <PublicCandidatePage token={token} />
        </Suspense>
      </ChunkBoundary>
    );
  }
  // Мобильная версия — только на телефоне; на компьютере вместо неё код,
  // чтобы открыть её на телефоне (до входа: логиниться ради заглушки незачем).
  if (typeof window !== "undefined" && isMobilePath(window.location.pathname) && !isPhone()) {
    return (
      <ChunkBoundary className="app-loading">
        <Suspense fallback={loading}>
          <PhoneOnly />
        </Suspense>
      </ChunkBoundary>
    );
  }
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

// Телефон, открывший `/` или `/chats` (в том числе ссылку «Открыть чат» из
// уведомления бота), уходит в мобильную версию — рисовать здесь нечего.
if (!redirectPhoneToMobile()) {
  // Новая сборка — сама, без Ctrl+Shift+R (lib/appUpdate).
  startUpdateWatch();
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <Root />
    </React.StrictMode>,
  );
}
